import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { LoadingSpinner } from './LoadingSpinner';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'destructive' | 'warning';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  children: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  children,
  className = '',
  disabled,
  ...props
}: ButtonProps) {
  const baseClasses =
    'inline-flex items-center justify-center font-medium rounded-full transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none';

  const variantClasses = {
    // `border border-transparent` keeps primary the SAME box height as the
    // bordered secondary in any layout (not just flex-stretch containers), so a
    // primary + secondary pair never differs by the border's 2px. Secondary uses
    // gray-300 so its edge reads as crisply as the filled primary on gray-50 bg.
    primary:
      'border border-transparent bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600',
    secondary:
      'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 focus-visible:ring-gray-500 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700',
    ghost:
      'text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus-visible:ring-gray-500 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-700',
    danger:
      'text-red-500 hover:text-red-600 hover:bg-red-50 focus-visible:ring-red-500 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20',
    // Filled red for the confirm action of a destructive dialog (Delete in a
    // "Delete X?" modal). `danger` is the quiet inline red-text action. red-600,
    // not 500: white on red-500 fails WCAG AA contrast for text-sm.
    destructive:
      'border border-transparent bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500 dark:bg-red-600 dark:hover:bg-red-700',
    // Filled amber for a "proceed with caution" confirm. Dark text, not white:
    // white on amber-500/600 is ~2.2-3.2:1 (fails AA); gray-900 on amber-500 is
    // ~8:1, on amber-600 (hover) ~5.6:1, and on the dark-mode amber-400 ~10:1.
    warning:
      'border border-transparent bg-amber-500 text-gray-900 hover:bg-amber-600 focus-visible:ring-amber-500 dark:bg-amber-400 dark:text-gray-900 dark:hover:bg-amber-500',
  };

  const sizeClasses = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-2.5 text-sm',
    lg: 'px-8 py-3 text-base',
  };

  return (
    <button
      className={cn(baseClasses, variantClasses[variant], sizeClasses[size], className)}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      {...props}
    >
      {isLoading && (
        <span className="mr-2">
          <LoadingSpinner size="sm" />
        </span>
      )}
      {children}
    </button>
  );
}
